import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiMenu, FiSearch, FiUser, FiSettings, FiLock,
  FiLogOut, FiMaximize, FiX,
} from 'react-icons/fi';
import { logout } from '../../store/slices/authSlice';
import { useBranding } from '../../contexts/BrandingContext';
import { useTranslation } from '../../contexts/LanguageContext';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import StaffWorkspaceBadge from '../../features/staff/StaffWorkspaceBadge';

/**
 * Top navbar — Airbnb-style chrome.
 *
 * Behaviour preserved from the legacy version: hamburger toggles the
 * mobile drawer, page title derived from the route, search toggle,
 * fullscreen toggle, language switcher, notification bell, user
 * dropdown with profile / preferences / sign-out.
 *
 * Visuals dropped the Sofbox `.iq-top-navbar` / `.iq-navbar-custom`
 * classes in favour of inline styles + brand-driven colors so the
 * topbar matches the public landing and host dashboard.
 */
const WMSNavbar = ({ showHamburger = false, onMenuToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const branding = useBranding();
  const { t } = useTranslation();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const userDropRef = useRef(null);
  const searchRef = useRef(null);

  const primary = branding.primary_color || '#7C3AED';
  const ring = `0 0 0 3px ${primary}40`;

  // Close dropdowns on outside click.
  useEffect(() => {
    const handleClick = (e) => {
      if (userDropRef.current && !userDropRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Derive a friendly page title from the route.
  const getPageTitle = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return 'Dashboard';
    return segments
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' '))
      .join(' / ');
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  // The auth user carries the person's name in ``full_name`` (``name`` is
  // usually empty, which is why the header used to fall back to the phone
  // number). Show the FIRST name on the bar; use it for the avatar initial too.
  const fullName = (user?.full_name || user?.name || '').trim();
  const firstName = fullName ? fullName.split(/\s+/)[0] : (user?.phone || 'User');
  const userInitial = fullName
    ? fullName.charAt(0).toUpperCase()
    : (user?.phone ? user.phone.slice(-2) : 'U');

  // Header chrome is admin-driven via Settings → Branding
  // (`header_bg_color` / `header_text_color`). The fallbacks below
  // only kick in if branding hasn't loaded yet — they match the
  // Khulja logo navy in BrandingContext so the first paint doesn't
  // flash white.
  const headerBg = branding.header_bg_color || '#1e4d8c';
  const headerFg = branding.header_text_color || '#ffffff';
  return (
    <header
      style={{
        background: headerBg,
        borderBottom: `1px solid ${headerBg}`,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        position: 'sticky', top: 0, zIndex: 50,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px', gap: 16,
      }}>
        {/* Left: hamburger + page title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {showHamburger && (
            <button
              type="button"
              aria-label="Open navigation"
              onClick={onMenuToggle}
              style={iconBtn(primary, ring, 38, headerFg)}
            >
              <FiMenu size={20} />
            </button>
          )}
          <h1 style={{
            margin: 0, fontSize: 16, fontWeight: 700, color: headerFg,
            letterSpacing: '-0.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {getPageTitle()}
          </h1>
        </div>

        {/* Right cluster */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* Staff workspace badge — staff-only */}
          <div style={{ marginRight: 8 }} className="d-none d-md-block">
            <StaffWorkspaceBadge />
          </div>

          {/* Search toggle */}
          <div ref={searchRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label={t('nav.search', 'Search')}
              onClick={() => setSearchOpen((v) => !v)}
              style={iconBtn(primary, ring, 38, headerFg)}
            >
              <FiSearch size={18} />
            </button>
            {searchOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                width: 320, background: '#fff', borderRadius: 14,
                border: '1px solid #ebebeb',
                boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                padding: 8, zIndex: 1050,
              }}>
                <form
                  onSubmit={(e) => e.preventDefault()}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <FiSearch size={16} style={{ color: '#717171', marginLeft: 8 }} />
                  <input
                    type="text"
                    placeholder={t('nav.search', 'Search here…')}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    autoFocus
                    style={{
                      flex: 1, border: 'none', outline: 'none',
                      fontSize: 14, padding: '8px 0', color: '#222',
                    }}
                  />
                  <button
                    type="button"
                    aria-label="Close search"
                    onClick={() => setSearchOpen(false)}
                    style={iconBtn(primary, ring, 32, '#222222')}
                  >
                    <FiX size={14} />
                  </button>
                </form>
              </div>
            )}
          </div>

          {/* Language */}
          <div style={{ position: 'relative' }}>
            <LanguageSwitcher />
          </div>

          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <NotificationBell />
          </div>

          {/* Fullscreen */}
          <button
            type="button"
            aria-label={t('nav.fullscreen', 'Toggle fullscreen')}
            onClick={toggleFullscreen}
            style={iconBtn(primary, ring, 38, headerFg)}
          >
            <FiMaximize size={16} />
          </button>

          {/* User pill — Airbnb-style menu pill */}
          <div ref={userDropRef} style={{ position: 'relative', marginLeft: 4 }}>
            <button
              type="button"
              aria-label={t('nav.openMenu', 'Open user menu')}
              aria-expanded={showUserDropdown}
              onClick={() => setShowUserDropdown((v) => !v)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '5px 5px 5px 12px', borderRadius: 999,
                border: '1px solid #ebebeb', background: '#fff',
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                outline: 'none',
              }}
              onFocus={(e) => { e.currentTarget.style.boxShadow = ring; }}
              onBlur={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)'; }}
            >
              <FiMenu size={14} color="#222" />
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: primary, color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
                position: 'relative', overflow: 'hidden', flexShrink: 0,
              }}>
                {/* Initial sits underneath; the avatar photo (if any) covers it.
                    If the image fails to load it hides itself and the initial
                    shows through — so there's never a blank circle. */}
                <span>{userInitial}</span>
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : null}
              </div>
              {/* Full name — shown on the bar (hidden on very small screens to
                  keep the header tidy). */}
              <span
                className="d-none d-sm-inline"
                style={{
                  fontSize: 13, fontWeight: 600, color: '#222',
                  maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', paddingRight: 6,
                }}
              >
                {firstName}
              </span>
            </button>

            {showUserDropdown && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                  width: 'min(280px, calc(100vw - 24px))',
                  background: '#fff', borderRadius: 14,
                  border: '1px solid #ebebeb',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                  padding: '8px 0', zIndex: 1050,
                }}
              >
                {/* Identity strip */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #ebebeb' }}>
                  <div style={{
                    fontSize: 14, fontWeight: 700, color: '#222',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {fullName || user?.phone || 'User'}
                  </div>
                  <div style={{
                    fontSize: 12, color: '#717171',
                    textTransform: 'capitalize', marginTop: 2,
                  }}>
                    {user?.role
                      ? user.role.replace(/_/g, ' ').toLowerCase()
                      : t('nav.role.member', 'Member')}
                  </div>
                </div>

                <MenuItem
                  icon={FiUser}
                  label={t('nav.myProfile', 'My Profile')}
                  onClick={() => { setShowUserDropdown(false); navigate('/profile'); }}
                />
                <MenuItem
                  icon={FiLock}
                  label={t('nav.changePassword', 'Change Password')}
                  onClick={() => { setShowUserDropdown(false); navigate('/profile?tab=security'); }}
                />
                <MenuItem
                  icon={FiSettings}
                  label={t('nav.preferences', 'Preferences')}
                  onClick={() => { setShowUserDropdown(false); navigate('/my-preferences'); }}
                />
                <div style={{ borderTop: '1px solid #ebebeb', marginTop: 4 }} />
                <MenuItem
                  icon={FiLogOut}
                  label={t('nav.signOut', 'Sign out')}
                  danger
                  onClick={handleLogout}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────

const iconBtn = (primary, ring, size = 38, fg = '#ffffff') => ({
  width: size, height: size, borderRadius: '50%',
  border: 'none', background: 'transparent', color: fg,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', outline: 'none',
  transition: 'background 120ms ease',
});

const MenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 12,
      width: '100%', padding: '10px 16px',
      border: 'none', background: 'transparent',
      color: danger ? '#b91c1c' : '#222',
      fontSize: 14, fontWeight: 500, textAlign: 'left',
      cursor: 'pointer', outline: 'none',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = '#f7f7f7'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    {Icon && <Icon size={16} style={{ flexShrink: 0, opacity: 0.8 }} />}
    <span>{label}</span>
  </button>
);

export default WMSNavbar;
